<template>
  <div class="app-content">
    <BlockHeroProducts v-if="product" />
    <AppBreadcrumbs
      :class="{
        'no-hero': !hasHero, 'has-padding': !hasHero,
      }"
    />

    <AppComponentRenderer
      v-if="data?.story?.content?.main_content"
      :data="data.story.content.main_content"
      :hide-heading="hideHeading"
      class="app-main-content"
      :class="{ 'has-padding': !hasHero }"
    />
  </div>
</template>

<script setup>
/*
    Fetch preset from storyblok and product from strapi
*/
const route = useRoute();
const { id } = route.params;

const storyStore = useStoryStore();
await storyStore.fetchCurrentStory(id);
const data = ref(storyStore.getCurrentStory);

onMounted(() => {
    // If version is draft, load the bridge to support storybloks preview
    const version = route.query._storyblok ? 'draft' : 'published';

    if (version === 'draft') {
        useStoryblokBridge(data.value.story.id, (evStory) => {
            data.value.story = evStory;
        });
    }
});

/*
Provide data to nested components
*/
const product = computed(() => storyStore.getConnectedStrapiData);
const shopItem = computed(() => storyStore.getConnectedShopitem);
provide('item', shopItem.value);
provide('product', product.value);

/*
    Redirect if product is not found
*/
const redirectLink = getStoryblokSetting('general', 'no_product_link')?.cached_url;

const router = useRouter();
if (!shopItem.value || !product.value) {
    router.push(`/${redirectLink}`);
}

/*
    Check if the page has a hero
*/
const hasHero = true;

/*
    SEO
*/
const { setData } = useSEO();

if (product.value) {
    setData({
        title: shopItem.value.seo?.seo_title || shopItem.value.name || '',
        description: shopItem.value.seo?.seo_description || shopItem.value.short_text || '',
        image: shopItem.value.seo?.seo_image || shopItem.value?.image || '',
        og_title: shopItem.value.seo?.seo_og_title || shopItem.value.name || '',
        og_description: shopItem.value.seo?.seo_og_description || shopItem.value.short_text || '',
        og_image: shopItem.value.seo?.seo_og_image || shopItem.value.teaser_image || '',
        twitter_title: shopItem.value.seo?.seo_og_twitter_title || shopItem.value.name || '',
        twitter_description: shopItem.value.seo?.seo_og_twitter_description || shopItem.value.short_text || '',
        twitter_image: shopItem.value.seo?.seo_og_twitter_image || shopItem.value.teaser_iamge || '',
    });
}

/*
    GTM
*/
const { sendEvent } = useGTM();
if (product.value) {
    sendEvent({
        event: 'view_item',
        ecommerce: {
            value: product.value?.price,
            items: [
                {
                    id: product.value?.id,
                    name: product.value?.name,
                    pharma_code: product.value?.pharma_code,
                    price: product.value?.price,
                    is_sendable: product.value?.is_sendable || false,
                    needs_prescription: product.value?.needs_prescription || false,
                    short_text: product.value?.short_text,
                    description: product.value?.description,
                },
            ],
            currency: 'CHF',
        },
    });
}
/*
    Hide Heading
*/
const hideHeading = computed(() => {
    if (!data.value?.story?.content?.strapi_data?.nutshell_text) {
        return true;
    }

    return false;
});

/*
  JSON-LD
 */
const runtimeConfig = useRuntimeConfig();

useJsonld({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.value?.name,
    image: product.value?.image,
    description: product.value?.description || product.value?.short_text,
    brand: {
        '@type': 'Brand',
        name: product.value?.brand?.name,
    },
    url: `${runtimeConfig.public.BASE_URL}/produkte/${product.value?.slug}`,
    gtin: product.value?.ean,
    category: product.value?.category?.name,
    offers: [
        {
            '@type': 'Offer',
            priceCurrency: 'CHF',
            price: product.value?.price,
        },
    ],
});
</script>

<style lang="scss" scoped>
.app-main-content {
    @include grid-layout();

    width: 100%;
    max-width: var(--grid-width);
    padding: 0 var(--page-padding);
    margin: 0 auto;

    @include fluid('margin-bottom', 80px, 120px);

    &.no-margin {
        margin: 0;
    }

    @include tablet-portrait{
        &.has-padding {
            @include fluid('padding-top', 140px, 278px);
        }
    }
}
</style>
